// The lesson's practice: the question sets a learner answers after the
// content, in the order the teacher put them, each checked on the spot. Each
// one (practice/PracticeItem.tsx) shows its heading, the instruction, the
// information given with it and the questions, answered in place
// (practice/Questions.tsx): radio buttons for single choice, checkboxes for
// multiple choice, a sentence with inputs in its gaps for gap filling, and a
// sentence with a drop-down list for each of its `selects` for select options.
//
// The lesson page is told how many are passed out of how many after the list
// loads and after every check, so its "Next lesson" button opens the moment the
// last one is passed when the lesson asks for that — no reload. A lesson the
// learner may not open shows nothing here: the page itself says it is shut.

import { useCallback, useEffect, useId, useState } from 'react'

import { ApiError, get } from '../../api'
import { useT } from '../../i18n'
import common from '../../i18n/common'
import practice from '../../i18n/practice'
import { messageOf } from '../../lib/ui'
import type { AttemptResult, LearnerPractice } from './practice/model'
import { PracticeItem } from './practice/PracticeItem'
import '../../styles/practice.css'

export type LessonPracticeProps = {
  lectureId: string
  /** Whether passing every practice is what opens the next lesson. */
  requirePractice: boolean
  /** How many of the lesson's practices are passed, out of how many, whenever that changes. */
  onProgress?: (passed: number, total: number) => void
}

type State =
  | { phase: 'loading' }
  | { phase: 'ready'; items: LearnerPractice[] }
  | { phase: 'hidden' }
  | { phase: 'failed'; message: string }

export default function LessonPractice({ lectureId, requirePractice, onProgress }: LessonPracticeProps) {
  const t = useT(practice)
  const tc = useT(common)
  const headId = useId()
  const [state, setState] = useState<State>({ phase: 'loading' })
  const [attempt, setAttempt] = useState(0)

  useEffect(() => {
    const ac = new AbortController()
    setState({ phase: 'loading' })
    get<{ items: LearnerPractice[] }>(`/learn/lectures/${lectureId}/practices`, ac.signal)
      .then((res) => setState({ phase: 'ready', items: res.items ?? [] }))
      .catch((err) => {
        if (ac.signal.aborted) return
        // A shut lesson (or one gone meanwhile) is the lesson page's to explain.
        if (err instanceof ApiError && (err.status === 403 || err.status === 404)) setState({ phase: 'hidden' })
        else setState({ phase: 'failed', message: messageOf(err, practice) })
      })
    return () => ac.abort()
  }, [lectureId, attempt])

  const items = state.phase === 'ready' ? state.items : null
  const passed = items?.filter((p) => p.passed).length ?? 0
  const total = items?.length ?? 0

  useEffect(() => {
    if (items) onProgress?.(passed, total)
  }, [items, passed, total, onProgress])

  const onChecked = useCallback((id: string, res: AttemptResult) => {
    setState((s) =>
      s.phase !== 'ready'
        ? s
        : {
            phase: 'ready',
            items: s.items.map((p) =>
              p.id === id
                ? {
                    ...p,
                    passed: p.passed || res.passed,
                    last_attempt: {
                      id: res.attempt_id,
                      correct: res.correct,
                      total: res.total,
                      score_pct: res.score_pct,
                      created_at: new Date().toISOString(),
                    },
                  }
                : p,
            ),
          },
    )
  }, [])

  if (state.phase === 'hidden' || (state.phase === 'ready' && state.items.length === 0)) return null
  if (state.phase === 'loading') return <section className="practices practices--loading" aria-busy="true" />
  if (state.phase === 'failed') {
    return (
      <section className="practices" aria-labelledby={headId}>
        <h2 id={headId}>{t('title')}</h2>
        <p className="practice__failure" role="alert">
          {t('loadFailed')} {state.message}
        </p>
        <button type="button" className="button button--quiet" onClick={() => setAttempt((n) => n + 1)}>
          {tc('retry')}
        </button>
      </section>
    )
  }

  return (
    <section className="practices" aria-labelledby={headId}>
      <header className="practices__head">
        <h2 id={headId}>{t('title')}</h2>
        <span className={passed === total ? 'practices__progress is-done' : 'practices__progress'}>
          {t('progress', { passed, n: total })}
        </span>
      </header>
      {requirePractice && passed < total && <p className="practices__note">{t('requiredNote')}</p>}
      {state.items.map((item, i) => (
        <PracticeItem key={item.id} item={item} number={i + 1} onChecked={(res) => onChecked(item.id, res)} />
      ))}
    </section>
  )
}
